import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, Flame, Gamepad2, ExternalLink, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/common/StatusBadge";
import Countdown from "@/components/common/Countdown";
import { themeFor } from "@/lib/communities";

export default function CommunityCard({ community, server, event, onOpenDiscord }) {
    const t = themeFor(community);
    const navigate = useNavigate();
    const slug = community.key.toLowerCase().replace(/_/g, "-");
    const online = (server?.status || "online") === "online";
    const purge = community.has_purge;

    return (
        <div
            data-testid={`community-card-${slug}`}
            className="tactical-card rounded-lg overflow-hidden animate-fade-up group"
            style={{ borderTop: `3px solid ${t.accent}` }}
        >
            <div className="relative p-5" style={{ background: t.gradient }}>
                <div className="flex items-start justify-between">
                    <div>
                        <div className="label-mono" style={{ color: t.accent2 }}>{community.platform}</div>
                        <h3 className="font-heading text-2xl font-extrabold uppercase tracking-wide text-foreground">
                            {community.name}
                        </h3>
                    </div>
                    <span className={`rounded-sm border px-2 py-0.5 label-mono ${t.tagClass}`}>{t.tag}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <StatusBadge status={online ? "online" : "offline"} testId={`community-status-${slug}`} />
                    {community.crossplay && (
                        <span className="label-mono text-muted-foreground border border-border rounded-sm px-2 py-0.5">
                            CROSSPLAY
                        </span>
                    )}
                </div>
            </div>

            <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <Metric icon={Gamepad2} label="Players" accent={t.accent}
                        value={`${server?.current_players ?? community.players_online}/${server?.max_players ?? community.max_players}`} />
                    <Metric icon={Users} label="Members" accent={t.accent} value={community.members} />
                </div>

                <div className="rounded-md border border-border bg-background/40 p-3">
                    {purge ? (
                        <Countdown target={event?.date || server?.save_wipe} label="Next Purge / Event" danger
                            testId={`purge-countdown-${slug}`} />
                    ) : (
                        <Countdown target={server?.save_wipe} label="Next Wipe"
                            testId={`wipe-countdown-${slug}`} />
                    )}
                </div>

                {event && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Flame size={14} style={{ color: t.accent }} />
                        <span className="truncate">Upcoming: <span className="text-foreground">{event.title}</span></span>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button data-testid={`open-community-${slug}`} size="sm"
                        className="font-heading uppercase tracking-wide text-black hover:opacity-90"
                        style={{ background: t.accent }}
                        onClick={() => navigate(`/player/communities?c=${community.key}`)}>
                        Open <ArrowRight size={14} className="ml-1" />
                    </Button>
                    <Button data-testid={`open-discord-${slug}`} size="sm" variant="outline"
                        className="font-heading uppercase tracking-wide border-discord/50 text-discord hover:bg-discord/10"
                        onClick={() => onOpenDiscord?.(community)}>
                        Discord <ExternalLink size={13} className="ml-1" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

function Metric({ icon: Icon, label, value, accent }) {
    return (
        <div className="flex items-center gap-2">
            <Icon size={16} style={{ color: accent }} />
            <div>
                <div className="label-mono text-muted-foreground">{label}</div>
                <div className="font-heading text-lg font-bold text-foreground tabular-nums">{value}</div>
            </div>
        </div>
    );
}
